<script setup lang="ts">
import type { components } from '@nrjx/api';
import type { ChartMessage } from '../../types';
import type { BarChartData, BarChartOptions } from '~/components/nrjx/chart/bar/index.vue';

// ================================
// Composables
// ================================
const { getConsumption } = coreConsumptionRepository();

// ================================
// Props
// ================================
const props = defineProps<{
  role: 'user' | 'assistant';
  message: ChartMessage;
}>();

// ================================
// Data
// ================================
const loading = ref(true);
const data = ref<components['schemas']['ConsumptionPoint'][]>([]);

// ================================
// Methods
// ================================
const loadData = async () => {
  loading.value = true;
  const response = await getConsumption({
    area_id: props.message.data.area_id,
    start: props.message.data.startDate,
    end: props.message.data.endDate,
    energy_type: props.message.data.energyType,
    aggregation_level: props.message.data.aggregationLevel,
  });
  if (response) data.value = response.items;
  loading.value = false;
};

// ================================
// Computed
// ================================
const chartOptions = computed((): BarChartOptions => {
  return {
    height: 'h-[50vh] max-h-[600px]',
    unit: 'kWh',
  };
});
const chartData = computed((): BarChartData[] => {
  return data.value.map((item) => ({
    key: item.start,
    barValues: [{ value: item.consumption.quantity }],
  }));
});

// ================================
// Lifecycle
// ================================
onMounted(() => {
  loadData();
});
</script>
<template>
  <div class="p-3 rounded-lg min-w-[50vw]" :class="[role === 'user' ? 'bg-primary-500 text-white' : 'bg-gray-200 text-gray-700']">
    <div class="p-4 bg-white rounded-lg">
      <nrjx-chart-bar :loading="loading" :data="chartData" :options="chartOptions" />
    </div>
  </div>
</template>
